/**
 * Category Service
 *
 * API calls for category management
 */

import { api } from '../lib/api';

// Types
export interface Category {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  bannerUrl: string | null;
  displayOrder: number;
  status: 'ACTIVE' | 'INACTIVE';
  parentId: string | null;
  catalogueUrl: string | null;
  catalogueGeneratedAt: string | null;
  createdAt: string;
  updatedAt: string;
  productCount: number;
  children?: Category[];
}

export interface CreateCategoryInput {
  name: string;
  slug: string;
  description?: string;
  bannerUrl?: string;
  parentId?: string;
  displayOrder?: number;
  status?: 'ACTIVE' | 'INACTIVE';
}

export interface UpdateCategoryInput {
  name?: string;
  slug?: string;
  description?: string;
  bannerUrl?: string;
  parentId?: string | null;
  displayOrder?: number;
  status?: 'ACTIVE' | 'INACTIVE';
}

export const categoryService = {
  /**
   * Get all categories (including inactive)
   */
  list: async (): Promise<Category[]> => {
    const response = await api.get<Category[]>('/admin/categories');
    return response.data || [];
  },

  /**
   * Get category by ID
   */
  getById: async (id: string): Promise<Category> => {
    const response = await api.get<Category>(`/admin/categories/${id}`);
    return response.data!;
  },

  /**
   * Create a new category
   */
  create: async (data: CreateCategoryInput): Promise<Category> => {
    const response = await api.post<Category>('/admin/categories', data);
    return response.data!;
  },

  /**
   * Update a category
   */
  update: async (id: string, data: UpdateCategoryInput): Promise<Category> => {
    const response = await api.patch<Category>(`/admin/categories/${id}`, data);
    return response.data!;
  },

  /**
   * Delete a category
   */
  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/categories/${id}`);
  },

  /**
   * Toggle category status
   */
  toggleStatus: async (id: string): Promise<Category> => {
    const response = await api.patch<Category>(`/admin/categories/${id}/status`);
    return response.data!;
  },

  /**
   * Reorder categories
   */
  reorder: async (orders: Array<{ id: string; displayOrder: number }>): Promise<void> => {
    await api.post('/admin/categories/reorder', { orders });
  },
};
